// @flow
import * as React from 'react';
import deepEqual from 'react-fast-compare';
import styled from 'styled-components';
import { sort } from 'utils/functional';
import Box from 'components/Box';
import Icon from 'components/Icon';
import Button, { LinkButton } from 'components/Button';
import { sprintf } from 'sprintf-js';
import Text, { BodySmall } from 'components/Text';
import Pill from 'components/Pill';
import Divider from 'components/Divider';
import { colors } from '../theme';

const { useEffect, useMemo, useCallback } = React;

type Facet = {
  title: string,
  name: string,
  options: Array<string>,
  optionTitles: { [string]: string },
  optionsArray: Array<{
    id: string,
    parentId: ?string,
    slug: string,
    name: string
  }>,
  id: string
};

const Category = styled(
  ({
    facet,
    slug,
    selected,
    toggle,
    ...props
  }: {
    facet: Facet,
    slug: string,
    selected: boolean,
    toggle: Function
  }) => (
    <Box
      component={Button}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      width={1}
      my="2px"
      height={50}
      textAlign="left"
      onClick={toggle}
      {...props}
    >
      <BodySmall>{facet.optionTitles[slug]}</BodySmall>
      <Icon
        name="close"
        width="6px"
        height="6px"
        stroke="rgba(255, 255, 255, 0.7)"
      />
    </Box>
  )
)`
  padding: 0 18px;
  ${props =>
    props.selected
      ? `
  color: white;
  background-color: ${colors.waterBlue};`
      : `
  color: ${colors.battleshipGrey};
  background-color: ${colors.platinum};
  &:hover,
  &:focus {
    color: ${colors.waterBlue} !important;
  }
  `}
`;

type Props = {
  facet: Facet,
  selectedOptions: Array<string>,
  setSearchFilter: Function,
  selectSearchFilterOption: Function,
  deselectSearchFilterOption: Function,
  selectAllSearchFilterOptions: Function
};

export default ({
  facet,
  selectedOptions,
  setSearchFilter,
  selectSearchFilterOption,
  deselectSearchFilterOption,
  selectAllSearchFilterOptions
}: Props) => {
  const categories = facet.optionsArray.filter(category => !category.parentId);

  const selectedCategories = categories.filter(category =>
    selectedOptions.includes(category.slug)
  );
  const selectedCategoryIds = selectedCategories.map(category => category.id);
  const selectedCategorySlugs = selectedCategories.map(
    category => category.slug
  );

  const allOptionsSelected = useMemo(
    () => deepEqual(sort(selectedOptions), sort(facet.options)),
    [facet.options, selectedOptions]
  );

  const relatedCategories = allOptionsSelected
    ? []
    : facet.optionsArray
        .filter(
          option =>
            selectedCategoryIds.includes(option.parentId) ||
            (option.parentId && selectedOptions.includes(option.slug))
        )
        .map(category => ({
          ...category,
          selected: selectedOptions.includes(category.slug)
        }));

  const toggleCategory = ({
    parentId,
    selected,
    slug
  }: {
    parentId: ?string,
    selected: boolean,
    slug: string
  }) => {
    if (selected) {
      deselectSearchFilterOption(facet.id, slug);
    } else {
      selectSearchFilterOption(facet.id, slug);
      if (selectedCategoryIds.includes(parentId)) {
        const parent = categories.find(category => category.id === parentId);
        if (parent) {
          deselectSearchFilterOption(facet.id, parent.slug);
        }
      }
    }
  };

  const selectAll = useCallback(
    () => {
      selectAllSearchFilterOptions(facet.id);
    },
    [facet.id, selectAllSearchFilterOptions]
  );

  useEffect(
    () => {
      if (selectedOptions.length === 0) {
        selectAll();
      }
    },
    [selectAll, selectedOptions]
  );

  if (!(categories && categories.length)) {
    return null;
  }

  return (
    <Box data-qa-id={`side-filter-facet-${facet.name}`}>
      <Box display="flex" justifyContent="space-between">
        <BodySmall>{facet.title}</BodySmall>

        {selectedCategoryIds.length > 0 && !allOptionsSelected && (
          <LinkButton onClick={selectAll}>
            <Text styleAs="h5">
              {/* TRANSLATORS: clear all filters in article search */ getText(
                'Clear'
              )}
            </Text>
          </LinkButton>
        )}
      </Box>

      <Box mt={16} data-qa-id="search-filter-section-filters">
        {categories.map(({ slug, id }) => {
          const selected =
            !allOptionsSelected &&
            selectedOptions.some(option => slug === option);
          const toggle = () => {
            if (selected) {
              if (deepEqual([slug], selectedCategorySlugs)) {
                selectAll();
              } else {
                deselectSearchFilterOption(facet.id, slug);
                relatedCategories
                  .filter(related => related.parentId === id)
                  .forEach(related => {
                    deselectSearchFilterOption(facet.id, related.slug);
                  });
              }
            } else {
              setSearchFilter(facet.id, [slug]);
            }
          };
          return (
            <Category
              key={slug}
              selected={selected}
              facet={facet}
              slug={slug}
              toggle={toggle}
              data-qa-id="side-filter"
              data-qa-selected={selected}
              data-qa-value={slug}
            />
          );
        })}
      </Box>

      {relatedCategories.length > 0 && !allOptionsSelected && (
        <Box>
          <Divider light my={36} />

          <Box display="flex" justifyContent="space-between">
            <BodySmall>{getText('Related')}</BodySmall>

            {relatedCategories.filter(cat => cat.selected).length > 0 && (
              <LinkButton onClick={selectAll}>
                <Text styleAs="h5">
                  {/* TRANSLATORS: clear all filters in article search */ getText(
                    'Clear'
                  )}
                </Text>
              </LinkButton>
            )}
          </Box>

          <Box mt={12} display="flex" flexWrap="wrap" mx="-2px">
            {relatedCategories.map(category => (
              <Box py="4px" px="2px" maxWidth="50%" key={category.id}>
                <Pill
                  onClick={() => toggleCategory(category)}
                  title={sprintf(getText(`Clear %(option)s filter`), {
                    option: category.name
                  })}
                  text={category.name}
                  active={category.selected}
                  outline
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
